/**
 * Shared components for Site KPI Tab
 *
 * Contains CircularProgress and PeriodToggle components.
 */

/**
 * Circular Progress Component for OTIF/defect rate metrics.
 */
export function CircularProgress({
  value,
  size = 80,
  strokeWidth = 8,
  color = '#10b981',
}: {
  value: number
  size?: number
  strokeWidth?: number
  color?: string
}) {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (value / 100) * circumference

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{value.toFixed(1)}%</span>
      </div>
    </div>
  )
}

/**
 * Period toggle button component for switching between time periods.
 */
export function PeriodToggle({
  options,
  selected,
  onChange,
}: {
  options: string[]
  selected: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex border rounded overflow-hidden text-xs">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`px-2 py-1 ${
            selected === option
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  )
}

/**
 * Helper function to parse month string to date range for API filtering.
 */
export const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const parts = monthStr.trim().split(' ')
    const monthIndex = monthNames.indexOf(parts[0])
    if (monthIndex === -1) return null
    let year: number
    if (parts.length >= 2 && parts[1]) {
      year = parseInt(parts[1])
      if (year < 100) year += 2000
    } else {
      year = new Date().getFullYear()
    }
    // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
    const startDate = new Date(year, monthIndex, 1)
    const endDate = new Date(year, monthIndex + 1, 0)
    const fmt = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      date_from: fmt(startDate),
      date_to: fmt(endDate),
    }
  } catch {
    return null
  }
}
